import { useEffect, useState } from "react";
import {
  CreatePaidTimeOffPolicyPayload,
  DC_COMPONENT_LABELS,
  PaidTimeOffPolicy,
  PTOPolicyAccrualType,
  PTOPolicyType,
  UpdatePaidTimeOffPolicyContract,
} from "winslow-core";
import "./AppPTOPolicyEditor.css";
import DatePicker from "react-datepicker";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  apiCreatePaidTimeOffPolicy,
  apiGetPaidTimeOffPoliciesForWorkspace,
  apiUpdatePaidTimeOffPolicy,
} from "../../../services/PaidTimeOffPolicyService";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { toastFail, toastSuccess } from "../../../config";
import toast from "react-hot-toast";
import { AppChanges } from "../app-changes/AppChanges";
import { set } from "lodash";
import { Radio } from "../../../ds2/components/Radio";
import { Checkbox } from "../../../ds2/components/Checkbox";

const PAID_TIME_OFF_POLICY_INITIAL: PaidTimeOffPolicy = {
  id: "",
  name: "",
  description: "",
  type: PTOPolicyType.VACATION,
  effectiveDate: new Date(),
  byAnniversary: true,
  accrualType: PTOPolicyAccrualType.BY_DAYS,
  accrualRate: 0,
  carryoverCap: undefined,
  accrualCap: undefined,
  workspaceId: "",
  createdAt: new Date(),
  updatedAt: new Date(),
};

export const PTOPolicyTypeDescription = {
  [PTOPolicyType.VACATION]: "Vacation",
  [PTOPolicyType.SICK]: "Sick",
  [PTOPolicyType.OTHER]: "Other",
};

export const PTOPolicyAccrualTypeDescription = {
  [PTOPolicyAccrualType.BY_HOURS]: "By Hours",
  [PTOPolicyAccrualType.BY_DAYS]: "By Days",
  [PTOPolicyAccrualType.UPFRONT]: "Up Front",
  [PTOPolicyAccrualType.UNLIMITED]: "Unlimited",
};

type AppPTOPolicyEditorProps = {
  paidTimeOffPolicyId?: string;
  onNavigation?: (
    newObjectName: DC_COMPONENT_LABELS, 
    newLabel: string,
    data: any
  ) => void;
  onCancel?: () => void;
};

export const AppPTOPolicyEditor = ({paidTimeOffPolicyId, onNavigation, onCancel}: AppPTOPolicyEditorProps) => {
  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const [isNew, setIsNew] = useState<boolean>(false);
  const [newPaidTimeOffPolicy, setNewPaidTimeOffPolicy] =
    useState<PaidTimeOffPolicy>(PAID_TIME_OFF_POLICY_INITIAL);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  useEffect(() => {
    dispatch(setUIStates({...uiStates, hrisLocation: "PTO Policy Editor"}));
  }, []);

  useEffect(() => {
    let usePolicyId : string | undefined = undefined;

    if (paidTimeOffPolicyId) {
      usePolicyId = paidTimeOffPolicyId;
    } else if (params?.ptoPolicyId) {
      usePolicyId = params.ptoPolicyId;
    }

    if (
      storeUser?.currentWorkspaceId && usePolicyId && usePolicyId !== "new"
    ) {
        (async () => {
          const ptoPolicies = await apiGetPaidTimeOffPoliciesForWorkspace(
            storeUser.currentWorkspaceId || ""
          );

          if (ptoPolicies) {
            const ptoPolicy = ptoPolicies.find((policy) => {
              return policy.id === usePolicyId;
            });

            if (ptoPolicy) {
              console.log(
                "[AppPTOPolicyEditor] ptoPolicy to be used: ",
                ptoPolicy
              );
              setNewPaidTimeOffPolicy(ptoPolicy);
            }
          }
        })();
    }

    if (usePolicyId === "new") {
      setIsNew(true);
    }
    
  }, [params, paidTimeOffPolicyId]);

  const policyTypeOptions: GenericSelectOption[] = Object.keys(
    PTOPolicyType
  ).map((key) => {
    return {
      displayName: PTOPolicyTypeDescription[key as PTOPolicyType],
      value: key,
      image: undefined,
      component: undefined,
    } as GenericSelectOption;
  });

  const handleSubmit = async () => {
    let newPolicy = null;

    console.log("newPaidTimeOffPolicy: ", newPaidTimeOffPolicy);
    // return;

    // Save the PTO policy
    if (isNew) {
      const payload: CreatePaidTimeOffPolicyPayload = {
        data: newPaidTimeOffPolicy,
        workspaceId: storeUser?.currentWorkspaceId || "",
      };

      newPolicy = await apiCreatePaidTimeOffPolicy(payload);

      if (!newPolicy) {
        toast("Error creating PTO policy", toastFail);
        return;
      }

      toast("PTO policy created successfully", toastSuccess);
    } else {
      newPolicy = await apiUpdatePaidTimeOffPolicy(
        newPaidTimeOffPolicy as UpdatePaidTimeOffPolicyContract["Body"]
      );

      if (!newPolicy) {
        toast("Error updating PTO policy", toastFail);
        return;
      }

      toast("PTO policy updated successfully", toastSuccess);
    }

    onNavigation ? onNavigation("pto-policy", "list", null) : navigate("/app/time-off");
  };

  const handleCancel = () => {
    onNavigation ? onNavigation("pto-policy", "list", null) : navigate("/app/time-off");
    onCancel?.();
  };

  const selectedPolicyType = policyTypeOptions.find((option: any) => {
    return newPaidTimeOffPolicy.type === option.value;
  });

  // NR note - removed BY_HOURS now as we don't have any hourly time features
  const accrualTypeOptions: GenericSelectOption[] = Object.keys(
    PTOPolicyAccrualType
  )
    .map((key) => {
      return {
        displayName:
          PTOPolicyAccrualTypeDescription[key as PTOPolicyAccrualType],
        value: key,
        image: undefined,
        component: undefined,
      } as GenericSelectOption;
    })
    .filter((option) => {
      return option.value !== PTOPolicyAccrualType.BY_HOURS;
    });

  const selectedAccrualPolicyType = accrualTypeOptions.find((option: any) => {
    return newPaidTimeOffPolicy.accrualType === option.value;
  });

  const savable =
    newPaidTimeOffPolicy.name &&
    // newPaidTimeOffPolicy.description &&
    newPaidTimeOffPolicy.effectiveDate &&
    newPaidTimeOffPolicy.type;

  console.log(
    "[AppPTOPolicyEditor] newPaidTimeOffPolicy: ",
    newPaidTimeOffPolicy
  );

  return (
    <div className="app-pto-policy-edit">
      {/* <div className="app-pto-policy-edit-header">
        {!isNew ? "Edit" : "Add New"} PTO Policy
      </div> */}
      <div className="app-pto-policy-edit-content">
        <div className="app-pto-policy-edit-left">
          {/* <div className="app-pto-policy-setting"> */}
            <label>Policy Name</label>
            <input
              type="text"
              value={newPaidTimeOffPolicy.name}
              onChange={(e) => {
                setNewPaidTimeOffPolicy({
                  ...newPaidTimeOffPolicy,
                  name: e.target.value,
                });
                setHasChanges(true);
              }}
            />
          {/* </div> */}

          {/* <div className="app-pto-policy-setting"> */}
            <label>Policy Description</label>
            <textarea
              // type="text"
              value={newPaidTimeOffPolicy.description}
              onChange={(e) => {
                setNewPaidTimeOffPolicy({
                  ...newPaidTimeOffPolicy,
                  description: e.target.value,
                });
                setHasChanges(true);
              }}
              rows={4}
            
            />
          {/* </div> */}

          {/* <div className="app-pto-policy-setting"> */}
            <label>Effective Date</label>
            <DatePicker
              selected={newPaidTimeOffPolicy.effectiveDate}
              dateFormat={"MM/dd/yyyy"}
              onChange={(date) => {
                setNewPaidTimeOffPolicy({
                  ...newPaidTimeOffPolicy,
                  effectiveDate: (date as Date) || new Date(),
                });
                setHasChanges(true);
              }}
              className="date-picker-input"
              wrapperClassName="app-date-picker-wrapper"
            />
          {/* </div> */}

          {/* <div className="app-pto-policy-setting"> */}
            <label>Policy Type</label>
            <GenericSelect
              options={policyTypeOptions}
              selected={selectedPolicyType}
              onChange={(value) => {
                setNewPaidTimeOffPolicy({
                  ...newPaidTimeOffPolicy,
                  type: value as PTOPolicyType,
                });
                setHasChanges(true);
              }}
              maxHeight="200px"
            />
          {/* </div> */}

          {/* <label>Calendar Year</label>
          <input
            name="byAnniversary"
            type="radio"
            checked={!newPaidTimeOffPolicy.byAnniversary}
            onChange={(e) => {
              setNewPaidTimeOffPolicy({
                ...newPaidTimeOffPolicy,
                byAnniversary: !e.target.checked,
              });
              setHasChanges(true);
            }}
          /> */}

        </div>
        <div className="app-pto-policy-edit-right">
          <label>By Anniversary</label>
          {/* <input
            type="radio"
            name="byAnniversary"
            checked={newPaidTimeOffPolicy.byAnniversary}
            onChange={(e) => {
              setNewPaidTimeOffPolicy({
                ...newPaidTimeOffPolicy,
                byAnniversary: e.target.checked,
              });
              setHasChanges(true);
            }}
          /> */}
          <Radio
            name="byAnniversary"
            choices={["Calendar Year", "By Anniversary"]}
            choice={newPaidTimeOffPolicy.byAnniversary ? "By Anniversary" : "Calendar Year"}
            onChange={(choice) => {
              setNewPaidTimeOffPolicy({
                ...newPaidTimeOffPolicy,
                byAnniversary: choice === "By Anniversary",
              });
              setHasChanges(true);
            }}
          />




          <label>Accrual Type</label>
          <GenericSelect
            options={accrualTypeOptions}
            selected={selectedAccrualPolicyType}
            onChange={(value) => {
              setNewPaidTimeOffPolicy({
                ...newPaidTimeOffPolicy,
                accrualType: value as PTOPolicyAccrualType,
              });
              setHasChanges(true);
            }}
            maxHeight="200px"
          />

          {(newPaidTimeOffPolicy.accrualType === PTOPolicyAccrualType.BY_DAYS ||
            newPaidTimeOffPolicy.accrualType ===
              PTOPolicyAccrualType.UPFRONT) && (
            <>
              <label>Accrual Rate By Days/Upfront</label>
              <input
                type="number"
                name="accrualRate"
                value={newPaidTimeOffPolicy.accrualRate}
                onChange={(e) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    accrualRate: parseFloat(e.target.value),
                  });
                  setHasChanges(true);
                }}
              />
            </>
          )}

          {newPaidTimeOffPolicy.accrualType ===
            PTOPolicyAccrualType.BY_HOURS && (
            <>
              <label>Accrual Rate By Hours</label>
              <input
                type="number"
                name="accrualRate"
                value={newPaidTimeOffPolicy.accrualRate}
                onChange={(e) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    accrualRate: parseFloat(e.target.value),
                  });
                  setHasChanges(true);
                }}
              />
            </>
          )}

          {newPaidTimeOffPolicy.accrualType !==
            PTOPolicyAccrualType.UNLIMITED && (
            <>
              <label>Carryover Cap</label>
              <Checkbox
                name="carryoverCap"
                choices={["Yes"]}
                choice={newPaidTimeOffPolicy.carryoverCap !== undefined ? "Yes" : undefined}
                onChange={(choice) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    carryoverCap: choice === "Yes" ? 0 : undefined,
                  });
                  setHasChanges(true);
                }}
              />
              {/* <input
                type="checkbox"
                name="carryoverCap"
                checked={newPaidTimeOffPolicy.carryoverCap !== undefined}
                // value={newPaidTimeOffPolicy.carryoverCap}
                onChange={(e) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    carryoverCap: e.target.checked ? 0 : undefined,
                  });
                  setHasChanges(true);
                }}
              /> */}

              {newPaidTimeOffPolicy.carryoverCap !== undefined && (
                <>
                  <label>Maximum for Carryover Cap</label>

                  <input
                    type="number"
                    name="carryoverCapValue"
                    value={newPaidTimeOffPolicy.carryoverCap}
                    onChange={(e) => {
                      setNewPaidTimeOffPolicy({
                        ...newPaidTimeOffPolicy,
                        carryoverCap: parseInt(e.target.value),
                      });
                      setHasChanges(true);
                    }}
                  />
                </>
              )}

              <label>Accrual Cap</label>
              <Checkbox
                name="accrualCap"
                choices={["Yes"]}
                choice={newPaidTimeOffPolicy.accrualCap !== undefined ? "Yes" : undefined}
                onChange={(choice) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    accrualCap: choice === "Yes" ? 0 : undefined,
                  });
                  setHasChanges(true);
                }}
              />
              {/* <input
                type="checkbox"
                name="accrualCap"
                checked={newPaidTimeOffPolicy.accrualCap !== undefined}
                // value={newPaidTimeOffPolicy.accrualCap}
                onChange={(e) => {
                  setNewPaidTimeOffPolicy({
                    ...newPaidTimeOffPolicy,
                    accrualCap: e.target.checked ? 0 : undefined,
                  });
                  setHasChanges(true);
                }}
              /> */}

              {newPaidTimeOffPolicy.accrualCap !== undefined && (
                <>
                  <label>Maximum for Accrual Cap</label>
                  <input
                    type="number"
                    name="accrualCapValue"
                    value={newPaidTimeOffPolicy.accrualCap}
                    onChange={(e) => {
                      setNewPaidTimeOffPolicy({
                        ...newPaidTimeOffPolicy,
                        accrualCap: parseInt(e.target.value),
                      });
                      setHasChanges(true);
                    }}
                  />
                </>
              )}
            </>
          )}
        </div>

        <div className="end-spacer">{}</div>
      </div>

      {hasChanges && (
        <AppChanges
          submitText={!isNew ? "Update" : "Add Policy"}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          saveDisabled={!savable}
        />
      )}
    </div>
  );
};
